import type {
  ArraySchema,
  EnumSchema,
  EnumValueSchema,
  JsonSchema,
  ObjectSchema,
  PrimitiveSchema,
  SchemaDef,
  SchemaProperty,
  SchemaRef,
} from '../types/schema';
import type { DefaultValue, EnumDef, EnumValue, FieldConstraints, FieldDef, FieldType, PrimitiveType, TypeDef } from '../types/model';
import { createId } from './modelUtils';

export const SCHEMA_URI = 'https://json-schema.org/draft/2020-12/schema';
const REF_PREFIX = '#/$defs/';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export const getRefId = (ref: string | undefined): string | null => {
  if (!ref || !ref.startsWith(REF_PREFIX)) {
    return null;
  }
  return ref.slice(REF_PREFIX.length);
};

export const isEnumDef = (def: SchemaDef): def is EnumSchema => {
  return isObject(def) && (Array.isArray((def as EnumSchema).oneOf) || Array.isArray((def as EnumSchema).enum));
};

export const isTypeDef = (def: SchemaDef): def is ObjectSchema => !isEnumDef(def);

const isObjectSchema = (value: unknown): value is ObjectSchema =>
  isObject(value) && (((value as ObjectSchema).type === 'object') || (value as ObjectSchema).properties !== undefined);

const normalizeEnumValues = (values: EnumValueSchema[]): EnumValueSchema[] =>
  values.map((value) => ({
    const: value.const || createId(),
    title: value.title || value.const || 'Value',
    description: value.description,
  }));

const normalizeEnumDef = (def: EnumSchema): EnumSchema => {
  const oneOf =
    def.oneOf && def.oneOf.length > 0
      ? normalizeEnumValues(def.oneOf)
      : Array.isArray(def.enum)
        ? normalizeEnumValues(def.enum.map((value) => ({ const: String(value), title: String(value) })))
        : [];
  return {
    ...def,
    type: 'string',
    title: def.title ?? '',
    description: def.description ?? '',
    oneOf,
  };
};

const normalizePropertySchema = (schema: SchemaProperty): SchemaProperty => {
  if ('$ref' in schema) {
    return { $ref: schema.$ref, 'x-id': (schema as SchemaRef)['x-id'] ?? createId() };
  }
  if ((schema as ArraySchema).type === 'array') {
    const arraySchema = schema as ArraySchema;
    return {
      ...arraySchema,
      type: 'array',
      items: arraySchema.items ? normalizePropertySchema(arraySchema.items) : { type: 'string' },
      'x-id': arraySchema['x-id'] ?? createId(),
    };
  }
  if ((schema as PrimitiveSchema).type) {
    const primitiveSchema = schema as PrimitiveSchema;
    return {
      ...primitiveSchema,
      'x-id': primitiveSchema['x-id'] ?? createId(),
    };
  }
  if (isObjectSchema(schema)) {
    const objectSchema = schema as ObjectSchema;
    const properties = objectSchema.properties ?? {};
    const normalizedProps: Record<string, SchemaProperty> = {};
    Object.entries(properties).forEach(([key, value]) => {
      normalizedProps[key] = normalizePropertySchema(value);
    });
    return {
      ...objectSchema,
      type: 'object',
      properties: normalizedProps,
      required: Array.isArray(objectSchema.required) ? objectSchema.required : [],
    };
  }
  return schema;
};

const normalizeObjectDef = (def: ObjectSchema): ObjectSchema => {
  if (Array.isArray(def.allOf) && def.allOf.length > 0) {
    let hasOwnObject = false;
    const normalizedAllOf = def.allOf.map((entry) => {
      if (isObjectSchema(entry)) {
        hasOwnObject = true;
        return normalizePropertySchema(entry) as ObjectSchema;
      }
      return entry;
    });
    if (!hasOwnObject) {
      normalizedAllOf.push(
        normalizePropertySchema({
          type: 'object',
          properties: {},
          required: [],
        }) as ObjectSchema
      );
    }
    return {
      ...def,
      title: def.title ?? '',
      description: def.description ?? '',
      'x-indexed': def['x-indexed'] ?? false,
      allOf: normalizedAllOf,
    };
  }
  return {
    ...def,
    title: def.title ?? '',
    description: def.description ?? '',
    'x-indexed': def['x-indexed'] ?? false,
    ...normalizePropertySchema(def),
  } as ObjectSchema;
};

export const normalizeSchema = (schema: JsonSchema): JsonSchema => {
  const defs = schema.$defs ?? {};
  const normalizedDefs: Record<string, SchemaDef> = {};
  Object.entries(defs).forEach(([id, def]) => {
    if (isEnumDef(def)) {
      normalizedDefs[id] = normalizeEnumDef(def);
      return;
    }
    if (isTypeDef(def)) {
      normalizedDefs[id] = normalizeObjectDef(def);
    }
  });
  return {
    ...schema,
    $schema: schema.$schema ?? SCHEMA_URI,
    $defs: normalizedDefs,
    'x-version': schema['x-version'] ?? createId(),
    'x-timestamp': schema['x-timestamp'] ?? new Date().toISOString(),
  };
};

export const createEmptySchema = (): JsonSchema => ({
  $schema: SCHEMA_URI,
  $defs: {},
  'x-version': createId(),
  'x-timestamp': new Date().toISOString(),
});

const getTypeParentId = (def: ObjectSchema): string | null => {
  if (!Array.isArray(def.allOf)) {
    return null;
  }
  for (const entry of def.allOf) {
    if (isObject(entry) && '$ref' in entry) {
      const parentId = getRefId((entry as SchemaRef).$ref);
      if (parentId) {
        return parentId;
      }
    }
  }
  return null;
};

const getOwnObjectSchema = (def: ObjectSchema): ObjectSchema => {
  if (Array.isArray(def.allOf)) {
    const own = def.allOf.find((entry) => isObjectSchema(entry));
    if (own && isObjectSchema(own)) {
      return own;
    }
  }
  return def;
};

const getPrimitiveFromSchema = (schema: PrimitiveSchema): PrimitiveType => {
  if (schema.type === 'integer') {
    return 'int';
  }
  if (schema.type === 'number') {
    return 'float';
  }
  if (schema.type === 'boolean') {
    return 'boolean';
  }
  return 'string';
};

const getFieldType = (
  schema: SchemaProperty,
  defs: Record<string, SchemaDef>
): FieldType => {
  if (isObject(schema) && typeof (schema as PrimitiveSchema)['x-refTypeId'] === 'string') {
    return { kind: 'reference', typeId: (schema as PrimitiveSchema)['x-refTypeId'] as string };
  }
  if (isObject(schema) && '$ref' in schema) {
    const refId = getRefId((schema as SchemaRef).$ref);
    if (refId) {
      const def = defs[refId];
      if (def && isEnumDef(def)) {
        return { kind: 'enum', enumId: refId };
      }
      return { kind: 'type', typeId: refId };
    }
  }
  if (isObject(schema) && (schema as PrimitiveSchema).type) {
    return { kind: 'primitive', primitive: getPrimitiveFromSchema(schema as PrimitiveSchema) };
  }
  return { kind: 'primitive', primitive: 'string' };
};

const getDefaultValue = (schema: SchemaProperty): DefaultValue => {
  if (!isObject(schema) || !('default' in schema)) {
    return null;
  }
  return (schema as PrimitiveSchema | ArraySchema).default as DefaultValue;
};

const getConstraints = (schema: SchemaProperty, isArray: boolean): FieldConstraints => {
  if (isArray && (schema as ArraySchema).type === 'array') {
    const arraySchema = schema as ArraySchema;
    return {
      minItems: arraySchema.minItems,
      maxItems: arraySchema.maxItems,
    };
  }
  const primitiveSchema = schema as PrimitiveSchema;
  if (primitiveSchema.type === 'string') {
    return {
      minLength: primitiveSchema.minLength,
      maxLength: primitiveSchema.maxLength,
    };
  }
  if (primitiveSchema.type === 'integer' || primitiveSchema.type === 'number') {
    return {
      minValue: primitiveSchema.minimum,
      maxValue: primitiveSchema.maximum,
    };
  }
  return {};
};

const getFieldId = (schema: SchemaProperty, fallback: string): string => {
  if (isObject(schema) && 'x-id' in schema && typeof schema['x-id'] === 'string') {
    return schema['x-id'] as string;
  }
  if (fallback) {
    return `field-${fallback}`;
  }
  return createId();
};

export const schemaToTypes = (schema: JsonSchema): TypeDef[] => {
  const defs = schema.$defs ?? {};
  return Object.entries(defs)
    .filter(([, def]) => isTypeDef(def))
    .map(([id, def]) => {
      const typeDef = def as ObjectSchema;
      const ownSchema = getOwnObjectSchema(typeDef);
      const properties = ownSchema.properties ?? {};
      const required = new Set(ownSchema.required ?? []);
      const fields: FieldDef[] = Object.entries(properties).map(([name, propSchema]) => {
        const isArray = isObject(propSchema) && (propSchema as ArraySchema).type === 'array';
        const targetSchema = isArray ? ((propSchema as ArraySchema).items ?? {}) : propSchema;
        return {
          id: getFieldId(propSchema, name),
          name,
          type: getFieldType(targetSchema as SchemaProperty, defs),
          isArray,
          isRequired: required.has(name),
          defaultValue: getDefaultValue(propSchema),
          constraints: getConstraints(isArray ? propSchema : targetSchema, isArray),
        };
      });
      return {
        id,
        name: typeDef.title ?? '',
        description: typeDef.description ?? '',
        parentTypeId: getTypeParentId(typeDef),
        fields,
        isIndexed: Boolean(typeDef['x-indexed']),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
};

export const schemaToEnums = (schema: JsonSchema): EnumDef[] => {
  const defs = schema.$defs ?? {};
  return Object.entries(defs)
    .filter(([, def]) => isEnumDef(def))
    .map(([id, def]) => {
      const enumDef = normalizeEnumDef(def as EnumSchema);
      const values: EnumValue[] = (enumDef.oneOf ?? []).map((entry) => ({
        id: entry.const,
        label: entry.title,
      }));
      return {
        id,
        name: enumDef.title ?? '',
        description: enumDef.description ?? '',
        values,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
};

export const toPrimitiveSchemaType = (primitive: PrimitiveType): PrimitiveSchema['type'] => {
  if (primitive === 'int') {
    return 'integer';
  }
  if (primitive === 'float') {
    return 'number';
  }
  if (primitive === 'boolean') {
    return 'boolean';
  }
  return 'string';
};

export const buildFieldSchema = (field: FieldDef): SchemaProperty => {
  let base: SchemaProperty;
  if (field.type.kind === 'primitive' || field.type.kind === 'reference') {
    const schemaType = field.type.kind === 'reference' ? 'string' : toPrimitiveSchemaType(field.type.primitive);
    base = {
      type: schemaType,
      'x-id': field.id,
    };
    if (field.type.kind === 'reference') {
      (base as PrimitiveSchema)['x-refTypeId'] = field.type.typeId;
    }
    if (schemaType === 'string') {
      (base as PrimitiveSchema).minLength = field.constraints.minLength;
      (base as PrimitiveSchema).maxLength = field.constraints.maxLength;
    }
    if (schemaType === 'integer' || schemaType === 'number') {
      (base as PrimitiveSchema).minimum = field.constraints.minValue;
      (base as PrimitiveSchema).maximum = field.constraints.maxValue;
    }
  } else {
    const refId = field.type.kind === 'enum' ? field.type.enumId : field.type.typeId;
    base = { $ref: `${REF_PREFIX}${refId}` };
    (base as SchemaRef)['x-id'] = field.id;
  }

  if (!field.isArray) {
    if (field.defaultValue !== null && field.defaultValue !== undefined) {
      (base as PrimitiveSchema).default = field.defaultValue;
    } else if ('default' in base) {
      delete (base as PrimitiveSchema).default;
    }
    return base;
  }

  const arraySchema: ArraySchema = {
    type: 'array',
    items: base,
    minItems: field.constraints.minItems,
    maxItems: field.constraints.maxItems,
    'x-id': field.id,
  };
  return arraySchema;
};

export const createTypeDef = (name: string): ObjectSchema => ({
  type: 'object',
  title: name,
  description: '',
  properties: {},
  required: [],
  'x-indexed': false,
});

export const createEnumDef = (name: string, values: EnumValue[]): EnumSchema => ({
  type: 'string',
  title: name,
  description: '',
  oneOf: values.map((value) => ({ const: value.id, title: value.label })),
});
